type RealtimeOptions = {
  document: Pick<Document, "visibilityState" | "addEventListener" | "removeEventListener">;
  window: Pick<Window, "setTimeout" | "clearTimeout" | "setInterval" | "clearInterval" | "addEventListener" | "removeEventListener">;
  createStream: () => Pick<EventSource, "addEventListener" | "close">;
  events: Record<string, EventListener>;
  refresh: () => void;
};

// Background tabs must release SSE slots, especially through HTTP/1.1 proxies.
export function startVisibleRealtime(options: RealtimeOptions): () => void {
  const { document, window, createStream, events, refresh } = options;
  let stream: ReturnType<RealtimeOptions["createStream"]> | null = null;
  let openingTimer: number | null = null;
  let pollingTimer: number | null = null;
  let retryTimer: number | null = null;
  let disposed = false;
  let pageHidden = false;
  const visible = () => !disposed && !pageHidden && document.visibilityState === "visible";
  const poll = () => { if (visible()) refresh(); };

  function closeStream() {
    if (openingTimer != null) window.clearTimeout(openingTimer);
    openingTimer = null;
    const previous = stream;
    stream = null;
    previous?.close();
  }

  function stopPolling() {
    if (pollingTimer != null) window.clearInterval(pollingTimer);
    if (retryTimer != null) window.clearTimeout(retryTimer);
    pollingTimer = retryTimer = null;
  }

  function suspend() {
    closeStream();
    stopPolling();
  }

  function startPolling() {
    closeStream();
    if (!visible()) return;
    if (pollingTimer == null) {
      poll();
      pollingTimer = window.setInterval(poll, 10_000);
    }
    if (retryTimer == null) retryTimer = window.setTimeout(() => {
      retryTimer = null;
      connect();
    }, 30_000);
  }

  function connect() {
    if (!visible() || stream) return;
    const connection = createStream();
    stream = connection;
    connection.addEventListener("open", () => {
      if (stream !== connection) return;
      if (openingTimer != null) window.clearTimeout(openingTimer);
      openingTimer = null;
      stopPolling();
    });
    connection.addEventListener("error", () => {
      if (stream === connection) startPolling();
    });
    for (const [name, handler] of Object.entries(events)) connection.addEventListener(name, (event) => {
      if (stream === connection && visible()) handler(event);
    });
    // A stalled stream must not keep one of the browser's limited slots forever.
    openingTimer = window.setTimeout(startPolling, 5_000);
  }

  const handleVisibility = () => {
    if (!visible()) suspend();
    else {
      poll();
      connect();
    }
  };
  const handlePageHide = () => { pageHidden = true; suspend(); };
  const handlePageShow = () => { pageHidden = false; handleVisibility(); };
  document.addEventListener("visibilitychange", handleVisibility);
  window.addEventListener("pagehide", handlePageHide);
  window.addEventListener("pageshow", handlePageShow);
  connect();
  return () => {
    disposed = true;
    suspend();
    document.removeEventListener("visibilitychange", handleVisibility);
    window.removeEventListener("pagehide", handlePageHide);
    window.removeEventListener("pageshow", handlePageShow);
  };
}
